import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminHeader from '../components/AdminHeader';
import ClientHeader from '../components/ClientHeader';
import NavSidebar from '../components/NavSidebar';
import { usePageMeta } from '../components/DashboardShell';
import { useView } from '../contexts/ViewContext';
import { getRiTotalRoutes } from '../api/auth';

import HeadlineMetrics from '../components/route-intelligence/HeadlineMetrics';
import AnalysisAlert from '../components/route-intelligence/AnalysisAlert';
import HealthBanner from '../components/route-intelligence/HealthBanner';
import MapOriginsTab from '../components/route-intelligence/tabs/MapOriginsTab';
import CorridorsTab from '../components/route-intelligence/tabs/CorridorsTab';
import TripEconomicsTab from '../components/route-intelligence/tabs/TripEconomicsTab';
import CongestionTab from '../components/route-intelligence/tabs/CongestionTab';
import MarketsTab from '../components/route-intelligence/tabs/MarketsTab';
import FuelIntelligenceTab from '../components/route-intelligence/tabs/FuelIntelligenceTab';
import { SkeletonDashboard } from '../components/shared/Skeleton';

const TABS = [
  { key: 'map',         label: 'Map & Origins' },
  { key: 'corridors',   label: 'Corridors' },
  { key: 'economics',   label: 'Trip Economics' },
  { key: 'congestion',  label: 'Congestion' },
  { key: 'markets',     label: 'Markets' },
  { key: 'fuel',        label: 'Fuel Intelligence' },
];

export default function RouteIntelligencePage() {
  usePageMeta({ headerActive: 'Route Intelligence', sidebarActive: 'route' });
  const view = useView();
  const username = localStorage.getItem('username') || 'User';
  const isClient = view.isClient;

  const clientId = view.supabaseClientId;
  const clientDisplayName = view.clientDisplayName;

  // The header's global date range, shared by every panel on the page. A `null` bound means
  // "no limit on that side" and is left out of the request.
  const { rangeStart, rangeEnd } = view;
  const range = useMemo(
    () => ({ from: rangeStart || null, to: rangeEnd || null }),
    [rangeStart, rangeEnd],
  );

  const [routeCheck, setRouteCheck] = useState({ loading: false, total: null });
  useEffect(() => {
    if (!clientId) {
      setRouteCheck({ loading: false, total: null });
      return;
    }
    let cancelled = false;
    setRouteCheck({ loading: true, total: null });
    // Deliberately range-free: this decides whether the property has route data AT ALL.
    // Passing the picker range would make a range with no routes read as "Route
    // Intelligence is not available for this property" and hide every tab.
    getRiTotalRoutes(clientId)
      .then((resp) => {
        if (cancelled) return;
        const d = resp?.data || {};
        setRouteCheck({ loading: false, total: Number(d.total_routes || 0) + Number(d.unreachable || 0) });
      })
      .catch(() => { if (!cancelled) setRouteCheck({ loading: false, total: 0 }); });
    return () => { cancelled = true; };
  }, [clientId]);
  const routesLoading = routeCheck.loading;
  const hasNoRouteData = Boolean(clientId) && !routesLoading && routeCheck.total === 0;

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'map';

  const updateParam = useCallback((key, value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value == null || value === '') next.delete(key);
      else next.set(key, String(value));
      return next;
    });
  }, [setSearchParams]);

  const setActiveTab = (tab) => updateParam('tab', tab);

  const tabContent = useMemo(() => {
    if (!clientId) return null;
    switch (activeTab) {
      case 'corridors':  return <CorridorsTab clientId={clientId} range={range} updateParam={updateParam} searchParams={searchParams} />;
      case 'economics':  return <TripEconomicsTab clientId={clientId} range={range} />;
      case 'congestion': return <CongestionTab clientId={clientId} range={range} />;
      case 'markets':    return <MarketsTab clientId={clientId} range={range} updateParam={updateParam} searchParams={searchParams} />;
      case 'fuel':       return <FuelIntelligenceTab clientId={clientId} range={range} />;
      case 'map':
      default:           return <MapOriginsTab clientId={clientId} range={range} updateParam={updateParam} searchParams={searchParams} />;
    }
  }, [activeTab, clientId, range, updateParam, searchParams]);

  const HeaderComp = isClient ? ClientHeader : AdminHeader;

  return (
    <main className="flex-1 min-w-0">
          <div className="px-4 md:px-6 lg:px-10 py-6 space-y-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Route Intelligence</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Guest drive routes, corridor performance, trip economics, congestion, and fuel cost.
              </p>
            </div>

            {clientId ? (
              routesLoading ? (
                <SkeletonDashboard cards={4} />
              ) : hasNoRouteData ? (
                <div className="w-full bg-white dark:bg-gray-900 rounded-xl border dark:border-gray-800 p-8 text-center shadow-sm">
                  <svg className="w-12 h-12 mx-auto text-[#32B1FF] mb-4" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="5" cy="6" r="2" />
                    <circle cx="19" cy="18" r="2" />
                    <path d="M5 8c0 7 14 1 14 8" />
                  </svg>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Route Intelligence is not available for this property
                  </h2>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    {clientDisplayName ? `"${clientDisplayName}" has no route data yet.` : 'The selected project has no route data yet.'} Switch to another property in the header to load the Route Intelligence dashboard.
                  </p>
                </div>
              ) : (
                <>
                  <HealthBanner clientId={clientId} />
                  <AnalysisAlert clientId={clientId} range={range} />
                  <HeadlineMetrics clientId={clientId} range={range} />
                </>
              )
            ) : (
              <div className="w-full bg-white dark:bg-gray-900 rounded-xl border dark:border-gray-800 p-8 text-center shadow-sm">
                <svg className="w-12 h-12 mx-auto text-[#32B1FF] mb-4" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="5" cy="6" r="2" />
                  <circle cx="19" cy="18" r="2" />
                  <path d="M5 8c0 7 14 1 14 8" />
                </svg>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Route Intelligence is not available for this property
                </h2>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {clientDisplayName ? `"${clientDisplayName}" does not have route data.` : 'The selected project does not have route data.'} Switch to another property in the header to load the Route Intelligence dashboard.
                </p>
              </div>
            )}

            {clientId && !routesLoading && !hasNoRouteData && (
              <div className="border-b border-gray-200 dark:border-gray-800">
                <div className="flex flex-wrap gap-1">
                  {TABS.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setActiveTab(t.key)}
                      className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                        activeTab === t.key
                          ? 'border-[#32B1FF] text-[#32B1FF]'
                          : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {clientId && !routesLoading && !hasNoRouteData && tabContent}
          </div>
        </main>
  );
}
